/**
 * ChapterViewModal — Premium branded chapter detail modal
 * Teal/gold theme with 8-step creation process timeline
 */

import React from 'react';
import { Button } from '@mui/material';
import {
  Close as CloseIcon,
  Mic as MicIcon,
  TextFields as TextIcon,
  PhotoCamera as PhotoIcon,
  Brush as StyleIcon,
  AspectRatio as LengthIcon,
  Person as PersonIcon,
  Palette as IllustrationIcon,
  Image as ImageIcon,
  MoodOutlined as MoodIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import './ChapterViewModal.css';

interface CreationSteps {
  inputMethod: { step: number; label: string; value: string | null };
  recording: { step: number; label: string; durationSeconds: number | null; rawWordCount: number | null; photosUploaded: number };
  writingStyle: { step: number; label: string; value: string | null };
  storyLength: { step: number; label: string; value: string | null };
  narrativePerspective: { step: number; label: string; value: string | null };
  illustrationStyle: { step: number; label: string; value: string | null };
  aiImages: { step: number; label: string; requested: number; generated: number };
  mood: { step: number; label: string; value: string | null };
}

interface ChapterDetail {
  id: string;
  title?: string;
  content?: string;
  status?: string;
  word_count?: number;
  writing_style?: string;
  illustration_style?: string;
  mood?: string;
  ai_transformation_count?: number;
  created_at?: string;
  updated_at?: string;
  user_email?: string;
  subscription_plan?: string;
  creationSteps?: CreationSteps;
}

interface ChapterViewModalProps {
  open: boolean;
  onClose: () => void;
  chapter: ChapterDetail | null;
}

const formatDuration = (seconds: number | null): string => {
  if (!seconds) return '';
  const min = Math.floor(seconds / 60);
  const sec = seconds % 60;
  return min > 0 ? `${min}m ${sec}s` : `${sec}s`;
};

const inputMethodIcon = (method: string | null) => {
  switch (method) {
    case 'voice': return <MicIcon sx={{ fontSize: 16 }} />;
    case 'photo': return <PhotoIcon sx={{ fontSize: 16 }} />;
    case 'hybrid': return <PhotoIcon sx={{ fontSize: 16 }} />;
    default: return <TextIcon sx={{ fontSize: 16 }} />;
  }
};

const ChapterViewModal: React.FC<ChapterViewModalProps> = ({ open, onClose, chapter }) => {
  if (!open || !chapter) return null;

  const steps = chapter.creationSteps;

  const stepItems = steps ? [
    {
      label: steps.inputMethod.label,
      value: steps.inputMethod.value,
      icon: inputMethodIcon(steps.inputMethod.value),
      filled: !!steps.inputMethod.value,
    },
    {
      label: steps.recording.label,
      value: steps.recording.durationSeconds
        ? formatDuration(steps.recording.durationSeconds)
        : steps.recording.rawWordCount
          ? `${steps.recording.rawWordCount} words`
          : null,
      detail: steps.recording.photosUploaded > 0 ? `${steps.recording.photosUploaded} photos` : undefined,
      icon: <MicIcon sx={{ fontSize: 16 }} />,
      filled: !!(steps.recording.durationSeconds || steps.recording.rawWordCount),
    },
    {
      label: steps.writingStyle.label,
      value: steps.writingStyle.value,
      icon: <StyleIcon sx={{ fontSize: 16 }} />,
      filled: !!steps.writingStyle.value,
    },
    {
      label: steps.storyLength.label,
      value: steps.storyLength.value,
      icon: <LengthIcon sx={{ fontSize: 16 }} />,
      filled: !!steps.storyLength.value,
    },
    {
      label: steps.narrativePerspective.label,
      value: steps.narrativePerspective.value?.replace('_', ' '),
      icon: <PersonIcon sx={{ fontSize: 16 }} />,
      filled: !!steps.narrativePerspective.value,
    },
    {
      label: steps.illustrationStyle.label,
      value: steps.illustrationStyle.value,
      icon: <IllustrationIcon sx={{ fontSize: 16 }} />,
      filled: !!steps.illustrationStyle.value,
    },
    {
      label: steps.aiImages.label,
      value: steps.aiImages.requested > 0 ? `${steps.aiImages.requested} requested, ${steps.aiImages.generated} generated` : null,
      icon: <ImageIcon sx={{ fontSize: 16 }} />,
      filled: steps.aiImages.requested > 0,
      gold: true,
    },
    {
      label: steps.mood.label,
      value: steps.mood.value,
      icon: <MoodIcon sx={{ fontSize: 16 }} />,
      filled: !!steps.mood.value,
    },
  ] : [];

  return (
    <div className="chapter-view-modal-overlay" onClick={onClose}>
      <div className="chapter-view-modal" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="chapter-modal-header">
          <button className="chapter-modal-close" onClick={onClose}>
            <CloseIcon sx={{ fontSize: 18 }} />
          </button>
          <h2 className="chapter-modal-title">{chapter.title || 'Untitled Chapter'}</h2>
          <div className="chapter-modal-meta-bar">
            <span className={`chapter-modal-status chapter-modal-status--${chapter.status || 'draft'}`}>
              {chapter.status || 'draft'}
            </span>
            <span className="chapter-modal-meta-item">
              <PersonIcon sx={{ fontSize: 14 }} />
              {chapter.user_email || 'Unknown'}
            </span>
            <span className="chapter-modal-meta-item">
              {(chapter.word_count || 0).toLocaleString()} words
            </span>
            {chapter.created_at && (
              <span className="chapter-modal-meta-item">
                {format(new Date(chapter.created_at), 'MMM d, yyyy')}
              </span>
            )}
          </div>
        </div>

        {/* Body */}
        <div className="chapter-modal-body">
          {/* 8-Step Creation Process */}
          {stepItems.length > 0 && (
            <>
              <h3 className="creation-steps-title">Creation Process</h3>
              <div className="creation-steps-timeline">
                {stepItems.map((item, i) => (
                  <div className="creation-step" key={i}>
                    <div className={`creation-step-dot ${item.filled ? (item.gold ? 'creation-step-dot--gold' : 'creation-step-dot--filled') : ''}`}>
                      {i + 1}
                    </div>
                    <span className="creation-step-label">{item.label}</span>
                    <div>
                      <span className={`creation-step-value ${!item.value ? 'creation-step-value--empty' : ''}`}>
                        {item.value || 'Not set'}
                      </span>
                      {item.detail && (
                        <div className="creation-step-detail">{item.detail}</div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* Content Preview */}
          <div className="chapter-content-section">
            <h3 className="chapter-content-label">Chapter Content</h3>
            <div className="chapter-content-preview">
              {chapter.content || 'No content available'}
            </div>
          </div>

          {/* AI Transformation Stats */}
          <div className="chapter-ai-row">
            <div className="chapter-ai-stat">
              <span className="chapter-ai-stat-label">AI Transformations</span>
              <span className="chapter-ai-stat-value">{chapter.ai_transformation_count || 0}</span>
            </div>
            <div className="chapter-ai-stat">
              <span className="chapter-ai-stat-label">Writing Style</span>
              <span className="chapter-ai-stat-value">{chapter.writing_style || '—'}</span>
            </div>
            <div className="chapter-ai-stat">
              <span className="chapter-ai-stat-label">Illustration</span>
              <span className="chapter-ai-stat-value">{chapter.illustration_style || '—'}</span>
            </div>
            <div className="chapter-ai-stat">
              <span className="chapter-ai-stat-label">Mood</span>
              <span className="chapter-ai-stat-value" style={{ textTransform: 'capitalize' }}>{chapter.mood || '—'}</span>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="chapter-modal-footer">
          <Button variant="outlined" onClick={onClose} sx={{ borderRadius: '8px' }}>
            Close
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ChapterViewModal;
